import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import selectors from 'redux/selectors/selectors';
import { updatePreferredTimezone } from 'redux/actions/eCommerceActions';
import { TIMEZONES } from 'shared/constants/timezones';
import { Item, Key, Value } from './styled';

const EditableValue = styled.div`
  display: flex;
  cursor: pointer;

  &:hover {
    color: #06abc1;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const Dropdown = styled.div`
  position: absolute;
  z-index: 100000;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #dbdbdb;
  padding: 0;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 6px 20px -9px rgba(0, 0, 0, 0.3);
`;

const SearchInput = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: solid 2px #06abc1;
  border-radius: 4px;
  font-size: 14px;
  background: #011741;
  color: #d3d3d3;

  &:focus {
    outline: none;
    background: #011741;
    color: #fff;
  }

  &::placeholder {
    color: #d3d3d3;
  }
`;

const DropdownItem = styled.div`
  padding: 12px;
  font-size: 14px;
  color: #000;
  border-bottom: solid 1px #06abc1;
  cursor: pointer;
  user-select: none;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: rgba(6, 171, 193, 0.1);
  }

  &.selected {
    background-color: rgba(6, 171, 193, 0.2);
    font-weight: 600;
  }
`;

const EditContainer = styled.div`
  position: relative;
  width: 100%;
`;

export function EditableTimezoneField() {
  const dispatch = useDispatch();
  const currentTimezone = useSelector(selectors.preferredTimezone);
  const [isEditing, setIsEditing] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedValue, setSelectedValue] = useState(currentTimezone);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const filteredTimezones = TIMEZONES.filter((tz) =>
    tz.label.toLowerCase().includes(searchValue.toLowerCase()),
  );

  const currentTimezoneLabel =
    TIMEZONES.find((tz) => tz.value === currentTimezone)?.label || currentTimezone;

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleEdit = () => {
    setIsEditing(true);
    setSearchValue('');
    setSelectedValue(currentTimezone);
    setIsDropdownOpen(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsDropdownOpen(false);
    setSearchValue('');
    setSelectedValue(currentTimezone);
  };

  const handleSelectTimezone = async (timezone) => {
    setSelectedValue(timezone.value);
    setSearchValue(timezone.label);
    setIsDropdownOpen(false);
    setIsEditing(false);

    if (timezone.value !== currentTimezone) {
      try {
        await dispatch(updatePreferredTimezone(timezone.value));
      } catch (error) {
        console.error('Failed to update timezone:', error);
      }
    }
  };

  const handleInputChange = (e) => {
    setSearchValue(e.target.value);
    setIsDropdownOpen(true);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <Item>
      <Key>Timezone</Key>
      {!isEditing ? (
        <EditableValue onClick={handleEdit}>{currentTimezoneLabel}</EditableValue>
      ) : (
        <Value>
          <EditContainer>
            <DropdownContainer ref={dropdownRef}>
              <SearchInput
                ref={inputRef}
                type="text"
                value={searchValue}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                placeholder="Search timezones..."
                onFocus={() => setIsDropdownOpen(true)}
              />
              {isDropdownOpen && (
                <Dropdown>
                  {filteredTimezones.length > 0 ? (
                    filteredTimezones.map((timezone) => (
                      <DropdownItem
                        key={timezone.value}
                        className={selectedValue === timezone.value ? 'selected' : ''}
                        onClick={() => handleSelectTimezone(timezone)}>
                        {timezone.label}
                      </DropdownItem>
                    ))
                  ) : (
                    <DropdownItem>No timezones found</DropdownItem>
                  )}
                </Dropdown>
              )}
            </DropdownContainer>
          </EditContainer>
        </Value>
      )}
    </Item>
  );
}
